import { useState, useRef, useEffect } from "react";

// ─── Storage keys ───────────────────────────────────────────────
const K = {
  sessions: "gwep_v2_sessions",
  prefs: "gwep_v2_prefs",
  projects: "gwep_v2_projects",
  user: "gwep_v2_user",
  log: "gwep_v2_log",
  sharedLog: "gwep_v2_shared_log",
};

const PROXY = "https://cold-poetry-7793.ashapiro.workers.dev";
const LOG_ENDPOINT = "https://cold-poetry-7793.ashapiro.workers.dev/log";

// Cost per 1M tokens (input+output blended estimate) in USD
const MODEL_COSTS = {
  "claude-opus-4-20250514":   { input: 15.0,  output: 75.0  },
  "claude-sonnet-4-20250514": { input: 3.0,   output: 15.0  },
  "claude-haiku-4-5-20251001":{ input: 0.8,   output: 4.0   },
};

function estimateCost(model, inputTokens, outputTokens) {
  const c = MODEL_COSTS[model] || MODEL_COSTS["claude-sonnet-4-20250514"];
  return (inputTokens / 1e6) * c.input + (outputTokens / 1e6) * c.output;
}

function formatCost(usd) {
  if (usd < 0.001) return "<$0.001";
  if (usd < 1) return "$" + usd.toFixed(3);
  return "$" + usd.toFixed(2);
}

// ─── Design tokens ──────────────────────────────────────────────
const C = {
  bg: "#0e0e11", surface: "#16161a", elevated: "#1e1e24",
  border: "rgba(255,255,255,0.07)", text: "#f0f0f0", muted: "#888899", dim: "#44445a",
  accent: "#7c6cfc", accentBg: "rgba(124,108,252,0.12)", accentBorder: "rgba(124,108,252,0.35)",
  green: "#2dd4a0", greenBg: "rgba(45,212,160,0.1)",
  amber: "#f5a623", amberBg: "rgba(245,166,35,0.1)",
  danger: "#ff5e5e", dangerBg: "rgba(255,94,94,0.1)",
  orange: "#f97316",
};

// ─── Constants ──────────────────────────────────────────────────
const PROJECTS = [
  { id: "general", label: "General", color: C.orange, emoji: "⚡" },
  { id: "cumbres", label: "Cumbres Herradura", color: "#60c8f5", emoji: "🏙" },
  { id: "radiatas", label: "Radiatas26", color: "#7c6cfc", emoji: "🏢" },
  { id: "somma", label: "Somma Arteaga", color: "#e879a0", emoji: "🏡" },
  { id: "reserva", label: "Reserva del Sur", color: "#2dd4a0", emoji: "🌿" },
  { id: "elite", label: "GWEP Elite Circle", color: "#f5a623", emoji: "🤝" },
];

const MODES = [
  { id: "corta", label: "Corta", tip: "Respuesta directa en 1-2 líneas." },
  { id: "normal", label: "Normal", tip: "Respuesta equilibrada en 2-4 párrafos." },
  { id: "larga", label: "Larga", tip: "Respuesta profunda con ejemplos y contexto." },
];

const PROJ_FACTS = {
  cumbres: "Cumbres Herradura — Camino Arenero 150, Naucalpan EdoMex. 6 torres, 120 depas/torre. Tipos A:76m² B:85m² C:97m² D:109m² PH:185m². Precios $4.4–$7 MDP, PHs hasta $11.8 MDP. Torre 1 vendida, Torre 2 entrega inmediata, Torre 3 preventa. cumbresherradura.com",
  radiatas: "Radiatas26 — Radiatas 26, Bosques de las Lomas, CDMX. Zona Santa Fe–Lomas–Bosques.",
  somma: `SOMMA Arteaga — Av. Arteaga y Salazar 1282, Contadero, Cuajimalpa de Morelos, CDMX. A 5 min de Santa Fe, 3 min del Hospital ABC Santa Fe. somma-arteaga.com

CONCEPTO: Desarrollo residencial que integra arquitectura, paisaje, interiorismo y sostenibilidad. Enfocado en calidad de vida, espacios habitables y conexión con la naturaleza. Evita discurso de lujo, prioriza la experiencia.

DIMENSIONES: Terreno de más de 2 hectáreas. 75% del predio destinado a áreas verdes y espacios sociales. 24 casas con terrenos de 450 m² a 800 m².

PRODUCTO: Casas de 480–565 m² construidos. Incluyen: 3-4 recámaras, sala con acceso a jardín, comedor, cocina equipada, family room con salida a jardín, estudio, cava-bar, terraza con cocina exterior, jardines privados (40–60% del terreno), 2 recámaras de servicio, baño para choferes, estacionamiento para 4 autos, bodega y área de lavado. Algunos lotes con vistas a barranca y copas de árboles.

AMENIDADES (Casa Club ~1,425 m², diseño Sofía Aspe + Bardasano Arquitectos): Alberca, carril de nado, jacuzzi, spa, gimnasio, cancha de pádel, terraza lounge, salón de eventos, áreas administrativas, cuartos de choferes, estacionamiento de visitas.

EQUIPO DE DISEÑO: Bardasano Arquitectos, Grupo GDU, Sofía Aspe (interiorismo).

SUSTENTABILIDAD: Paneles solares, calentadores solares, LED, captación pluvial, tratamiento de aguas grises, conservación de biodiversidad (8,500 m²), +200 árboles, tanque de tormenta 150 m³.

INFRAESTRUCTURA: Sistema hidráulico 24/7 sin tinacos, cisternas centralizadas, planta de tratamiento de aguas grises. Dos saunas 7.5 kW, tina fría con filtrado/ozono/LED/bomba de calor 50,000 BTU, generadores de vapor Burnham Independence 62 MBH.

SEGURIDAD: Vigilancia 24/7, caseta de control vehicular y peatonal.

PERSONALIZACIÓN: Pisos, carpinterías, mármoles, cocinas, cava-bar.

ASPECTOS COMERCIALES: Precios desde ~$35 MDP. Estrategia de comunicación: mostrar sobre decir, evitar términos como "lujo" o "exclusividad".

OPERACIÓN: Coordinación central Bardasano Arquitectos. Tiempos de respuesta ideales 72h. Recorridos semanales de obra con minutas. Especialistas: Inesco, Inversa, GDU.`,
  reserva: "Reserva del Sur — Tláhuac, CDMX. Zona Tláhuac–Xochimilco–sur CDMX. reservadelsur.com.mx",
  elite: "GWEP Elite Circle — Canal oficial de comercializacion con brokers externos de Grupo GWEP. Canal estructurado para escalar ventas a traves de asesores independientes e inmobiliarias externas. OBJETIVO: Multiplicar ventas sin aumentar estructura interna. Mantener control de leads. Evitar conflictos de atribucion. BROKERS: Registro oficial requerido (webinar → formulario → CRM → validacion → autorizacion). Nadie vende sin registro. CLIENTES: Un cliente = un registro. Se valida contra base interna. Cliente debe registrarse ANTES de la visita. FLUJO: Registro → Confirmacion → Visita → Seguimiento → Apartado → Contrato → Enganche → Escrituracion → Comision. COMISIONES: 50/50. Primera parte al avance comercial, segunda a escritura. Requiere expediente completo y validacion. ACTIVIDADES: Webinars semanales, recorridos mensuales, capacitaciones bimestrales, eventos con brokers. TECNOLOGIA: CRM (HubSpot/Zenvia), formulario oficial, ERP financiero, materiales institucionales. PROYECTOS: Cumbres Herradura, Somma Arteaga, Radiatas26, Reserva del Sur y futuros. FILOSOFIA: Orden, Trazabilidad, Confianza, Reglas claras, Escalabilidad.",
};

const LOADING_NORMAL = ["Analizando...", "Mejorando prompt...", "Generando respuesta..."];
const LOADING_DEEP = ["Buscando en internet...", "Analizando fuentes...", "Verificando información...", "Construyendo respuesta..."];

// ─── Helpers ────────────────────────────────────────────────────
function newSession() {
  return { id: Date.now(), title: "Nueva sesión", createdAt: Date.now(), history: [], project: "general", mode: "normal" };
}

function stripCites(t) {
  if (!t) return t;
  return t.replace(/<cite[^>]*>([^<]*)<\/cite>/gi, "$1").replace(/<cite[^>]*\/>/gi, "");
}

function extractText(content) {
  if (!content) return "";
  const blocks = Array.isArray(content) ? content : [];
  const texts = blocks.filter(b => b.type === "text").map(b => b.text);
  if (texts.join("").trim()) return texts.join("");
  const toolResults = blocks.filter(b => b.type === "tool_result").map(b =>
    Array.isArray(b.content) ? b.content.filter(c => c.type === "text").map(c => c.text).join("") : b.content || ""
  );
  return toolResults.join("").trim();
}

function inlineFormat(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (/^\*[^*]+\*$/.test(p)) return <em key={i} style={{ color: "#888899" }}>{p.slice(1, -1)}</em>;
    if (/^`[^`]+`$/.test(p)) return <code key={i} style={{ background: "rgba(124,108,252,0.15)", color: "#a78bfa", borderRadius: 4, padding: "1px 5px", fontSize: 12, fontFamily: "monospace" }}>{p.slice(1, -1)}</code>;
    return p;
  });
}

function isTableRow(line) { return line.trim().startsWith("|") && line.trim().endsWith("|"); }
function isSepRow(line) { return /^[\|\s\-:]+$/.test(line); }
function parseRow(line) { return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map(c => c.trim()); }

function renderMd(text, msgId) {
  if (!text) return null;
  const lines = text.split("\n");
  const els = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (isTableRow(line)) {
      const tls = [];
      while (i < lines.length && (isTableRow(lines[i]) || isSepRow(lines[i]))) { tls.push(lines[i]); i++; }
      let headers = [], rows = [], sep = false;
      tls.forEach(tl => {
        if (isSepRow(tl) && !isTableRow(tl)) { sep = true; return; }
        const cols = parseRow(tl);
        if (!sep) headers.push(cols); else rows.push(cols);
      });
      if (!headers.length && rows.length) headers.push(rows.shift());
      els.push(<div key={"t" + i} style={{ overflowX: "auto", marginBottom: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          {headers.length > 0 && <thead><tr>{headers[0].map((h, hi) => <th key={hi} style={{ background: "rgba(124,108,252,0.2)", color: "#c4b8ff", padding: "7px 12px", textAlign: "left", borderBottom: "2px solid rgba(124,108,252,0.4)", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>)}</tr></thead>}
          <tbody>{rows.map((row, ri) => <tr key={ri} style={{ background: ri % 2 === 0 ? "transparent" : "rgba(255,255,255,0.03)" }}>{row.map((cell, ci) => <td key={ci} style={{ padding: "6px 12px", borderBottom: "1px solid rgba(255,255,255,0.06)", color: "#e0e0e0", verticalAlign: "top" }}>{inlineFormat(cell)}</td>)}</tr>)}</tbody>
        </table>
      </div>);
      continue;
    }
    if (/^###\s/.test(line)) { els.push(<p key={i} id={msgId ? "s-" + msgId + "-" + i : undefined} style={{ fontWeight: 700, fontSize: 13, color: "#a78bfa", margin: "10px 0 4px" }}>{line.replace(/^###\s/, "")}</p>); }
    else if (/^##\s/.test(line)) { els.push(<p key={i} id={msgId ? "s-" + msgId + "-" + i : undefined} style={{ fontWeight: 700, fontSize: 14, color: "#7c6cfc", margin: "12px 0 4px" }}>{line.replace(/^##\s/, "")}</p>); }
    else if (/^#\s/.test(line)) { els.push(<p key={i} style={{ fontWeight: 800, fontSize: 15, color: "#7c6cfc", margin: "14px 0 6px" }}>{line.replace(/^#\s/, "")}</p>); }
    else if (/^[-*]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i])) { items.push(<li key={i} style={{ marginBottom: 3 }}>{inlineFormat(lines[i].replace(/^[-*]\s/, ""))}</li>); i++; }
      els.push(<ul key={"ul" + i} style={{ margin: "4px 0 8px", paddingLeft: 18, fontSize: 14, lineHeight: 1.7 }}>{items}</ul>);
      continue;
    }
    else if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) { items.push(<li key={i} style={{ marginBottom: 3 }}>{inlineFormat(lines[i].replace(/^\d+\.\s/, ""))}</li>); i++; }
      els.push(<ol key={"ol" + i} style={{ margin: "4px 0 8px", paddingLeft: 18, fontSize: 14, lineHeight: 1.7 }}>{items}</ol>);
      continue;
    }
    else if (line.trim() === "") { els.push(<div key={i} style={{ height: 6 }} />); }
    else { els.push(<p key={i} style={{ margin: "2px 0", fontSize: 14, lineHeight: 1.7, color: "#f0f0f0" }}>{inlineFormat(line)}</p>); }
    i++;
  }
  return els;
}

function extractSections(text) {
  if (!text) return [];
  return text.split("\n").reduce((acc, line, idx) => {
    if (/^##\s/.test(line)) acc.push({ title: line.replace(/^##\s/, ""), idx, sub: false });
    else if (/^###\s/.test(line)) acc.push({ title: line.replace(/^###\s/, ""), idx, sub: true });
    return acc;
  }, []);
}

// ─── Tooltip ────────────────────────────────────────────────────
function Tip({ text, children, dir }) {
  const [show, setShow] = useState(false);
  const down = dir === "down";
  return (
    <div style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && text && (
        <div style={{ position: "absolute", top: down ? "calc(100% + 6px)" : undefined, bottom: down ? undefined : "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)", background: "#1e1e2e", border: "1px solid rgba(124,108,252,0.4)", borderRadius: 7, padding: "5px 10px", fontSize: 11, color: "#c4b8ff", whiteSpace: "nowrap", zIndex: 9999, pointerEvents: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.5)" }}>
          {text}
          <div style={{ position: "absolute", top: down ? undefined : "100%", bottom: down ? "100%" : undefined, left: "50%", transform: "translateX(-50%)", borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: down ? undefined : "5px solid rgba(124,108,252,0.4)", borderBottom: down ? "5px solid rgba(124,108,252,0.4)" : undefined }} />
        </div>
      )}
    </div>
  );
}

// ─── Build system prompt ─────────────────────────────────────────
function buildPrompt(prefs, project, projectsData, mode, deep) {
  const proj = PROJECTS.find(p => p.id === project);
  const modeInstr = { corta: "Responde en máximo 1-2 líneas. Solo el dato esencial.", normal: "Responde en 2-4 párrafos equilibrados.", larga: "Elabora con profundidad, ejemplos y contexto completo." };

  let projCtx = "";
  if (project === "general") {
    projCtx = "MODO GENERAL: Conoces todos los proyectos de Grupo GWEP y las preferencias del usuario. Mejora el prompt y mezcla el conocimiento interno con lo que encuentres.\n\n";
    projCtx += "PROYECTOS GWEP:\n" + Object.entries(PROJ_FACTS).map(([k, v]) => `- ${v}`).join("\n") + "\n\n";
    const allEntries = [];
    PROJECTS.filter(p => p.id !== "general").forEach(p => {
      const entries = (projectsData[p.id] || []);
      if (entries.length) {
        allEntries.push(`=== ${p.label} ===`);
        entries.forEach(e => {
          if (e.type === "nota") allEntries.push("Nota: " + e.text);
          if (e.type === "link") allEntries.push("Link: " + e.title + " → " + e.url + (e.desc ? " [" + e.desc + "]" : ""));
        });
      }
    });
    if (allEntries.length) projCtx += "BASE DE CONOCIMIENTO:\n" + allEntries.join("\n") + "\n\n";
  } else if (PROJ_FACTS[project]) {
    projCtx = "PROYECTO ACTIVO: " + PROJ_FACTS[project] + "\n";
    const entries = projectsData[project] || [];
    if (entries.length) {
      projCtx += "\nBASE DE CONOCIMIENTO DEL PROYECTO:\n";
      entries.forEach(e => {
        if (e.type === "nota") projCtx += "- Nota: " + e.text + "\n";
        if (e.type === "link") projCtx += "- Link: " + e.title + " → " + e.url + (e.desc ? " [" + e.desc + "]" : "") + "\n";
      });
    }
    projCtx += "\n";
  }

  const prefsCtx = prefs.trim() ? "PREFERENCIAS DEL USUARIO:\n" + prefs + "\n\n" : "";

  return `# Prompt Master — Agente Interno Grupo GWEP

Eres Prompt Master, herramienta de IA de uso EXCLUSIVAMENTE INTERNO para el equipo de Grupo GWEP.

TU MISIÓN:
1. Detectar la intención real del usuario
2. Mejorar su prompt para obtener la respuesta más útil posible
3. SIEMPRE responder con información concreta o pedir clarificación específica
4. Nunca devolver respuestas vacías — si hay un error, explícalo claramente
5. Aprender de las correcciones del equipo

${prefsCtx}${projCtx}MODO: ${modeInstr[mode] || modeInstr.normal}
${deep ? "BÚSQUEDA WEB ACTIVA: Busca información actualizada, cita fuentes relevantes.\n" : ""}
REGLAS:
1. INTENCIÓN: Detecta siempre qué quiere lograr el colaborador (informar/crear/decidir/comparar/resolver).
2. MEJORA DE PROMPT: Reformula la pregunta para obtener la respuesta más útil. Si ya es perfecta, marca prompt_era_optimo:true.
3. RESPUESTA OBLIGATORIA: SIEMPRE responde con contenido útil O haz UNA pregunta de clarificación concreta. Nunca dejes respuesta vacía.
4. ERRORES: Si algo falla o no tienes información, explica exactamente qué falta y cómo obtenerlo.
5. PREMISA INCORRECTA: Si la pregunta asume algo falso, corrígelo antes de responder.
6. PUNTO CLAVE: Resume lo más importante en máx 20 palabras en punto_clave.
7. DATOS: Lista números, fechas y porcentajes en datos_numericos.
8. IDIOMA: Responde siempre en el idioma del colaborador.
9. APRENDIZAJE: Si el usuario hace una corrección, captúrala en nueva_info_proyecto con el proyecto al que pertenece.
10. NUNCA inventes precios ni disponibilidad sin confirmar.

JSON sin backticks:
{"tipo":"...","intencion":"...","prompt_mejorado":"...","prompt_era_optimo":false,"punto_clave":"...","necesita_aclaracion":null,"premisa_incorrecta":null,"datos_numericos":[],"nueva_info_proyecto":null,"proyecto_info":null,"respuesta":"..."}`;
}

// ─── Main App ────────────────────────────────────────────────────
export default function App() {
  const [userName, setUserName] = useState("");
  const [userInput, setUserInput] = useState("");
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [prefs, setPrefs] = useState("");
  const [prefsInput, setPrefsInput] = useState("");
  const [projectsData, setProjectsData] = useState({ cumbres: [], radiatas: [], somma: [], reserva: [], elite: [] });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingIdx, setLoadingIdx] = useState(0);
  const [tab, setTab] = useState("chat");
  const [activeProject, setActiveProject] = useState("general");
  const [mode, setMode] = useState("normal");
  const [deep, setDeep] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [copied, setCopied] = useState(null);
  const [feedback, setFeedback] = useState({});
  const [feedbackPanel, setFeedbackPanel] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [fullscreen, setFullscreen] = useState(null);
  const [activeProj, setActiveProj] = useState("cumbres");
  const [projInput, setProjInput] = useState("");
  const [projLinkTitle, setProjLinkTitle] = useState("");
  const [projLinkDesc, setProjLinkDesc] = useState("");
  const [projType, setProjType] = useState("nota");
  const [projSearch, setProjSearch] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [onboardStep, setOnboardStep] = useState(0);
  const bottomRef = useRef(null);
  const loadRef = useRef(null);
  const fileRef = useRef(null);
  const [attached, setAttached] = useState(null);
  const [myLog, setMyLog] = useState([]);
  const [sharedLog, setSharedLog] = useState([]);
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [adminDateFilter, setAdminDateFilter] = useState("all");
  const [adminPass, setAdminPass] = useState("");
  const [adminErr, setAdminErr] = useState(false);
  const ADMIN_PASS = "Sonata278!";

  const activeSession = sessions.find(s => s.id === activeId) || null;
  const history = activeSession?.history || [];
  const proj = PROJECTS.find(p => p.id === activeProject) || PROJECTS[0];

  // Load from storage
  useEffect(() => {
    async function load() {
      try { const u = localStorage.getItem(K.user); if (u) { setUserName(u); setUserInput(u); } } catch (e) {}
      try { const s = localStorage.getItem(K.sessions); if (s) { const p = JSON.parse(s); setSessions(p); if (p.length) setActiveId(p[0].id); } } catch (e) {}
      try { const pr = localStorage.getItem(K.prefs); if (pr) { setPrefs(pr); setPrefsInput(pr); } } catch (e) {}
      try { const pd = localStorage.getItem(K.projects); if (pd) setProjectsData(JSON.parse(pd)); } catch (e) {}
      try { const ml = localStorage.getItem(K.log); if (ml) setMyLog(JSON.parse(ml)); } catch (e) {}
    }
    load();
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [history, loading]);

  useEffect(() => {
    if (loading) {
      const arr = deep ? LOADING_DEEP : LOADING_NORMAL;
      loadRef.current = setInterval(() => setLoadingIdx(i => (i + 1) % arr.length), 900);
    } else { clearInterval(loadRef.current); setLoadingIdx(0); }
    return () => clearInterval(loadRef.current);
  }, [loading, deep]);

  function toast(msg, color = C.green) {
    const id = Date.now();
    setToasts(t => [...t, { id, msg, color }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 2800);
  }

  function persist(s) {
    try { localStorage.setItem(K.sessions, JSON.stringify(s.map(x => ({ ...x, history: x.history.slice(-40) })))); } catch (e) {}
  }

  function updateSession(id, fn) {
    setSessions(prev => { const next = prev.map(s => s.id === id ? fn(s) : s); persist(next); return next; });
  }

  function createSession() {
    const s = newSession();
    setSessions(prev => { const next = [s, ...prev]; persist(next); return next; });
    setActiveId(s.id);
    setTab("chat");
  }

  function deleteSession(id) {
    setSessions(prev => {
      const next = prev.filter(s => s.id !== id);
      persist(next);
      if (activeId === id) setActiveId(next[0]?.id || null);
      return next;
    });
  }

  async function savePrefs() {
    setPrefs(prefsInput);
    try { try{localStorage.setItem(K.prefs, prefsInput);}catch(e){} toast("Preferencias guardadas"); } catch (e) {}
  }

  async function saveProjData(data) {
    setProjectsData(data);
    try { try{localStorage.setItem(K.projects, JSON.stringify(data));}catch(e){} toast("Proyecto actualizado"); } catch (e) {}
  }

  function addEntry() {
    const pid = activeProj;
    let entry = null;
    if (projType === "nota" && projInput.trim()) {
      entry = { id: Date.now(), type: "nota", text: projInput.trim(), author: userName || "Colaborador", ts: Date.now() };
      setProjInput("");
    } else if (projType === "link" && projInput.trim() && projLinkTitle.trim() && projLinkDesc.trim()) {
      entry = { id: Date.now(), type: "link", url: projInput.trim(), title: projLinkTitle.trim(), desc: projLinkDesc.trim(), author: userName || "Colaborador", ts: Date.now() };
      setProjInput(""); setProjLinkTitle(""); setProjLinkDesc("");
    } else { toast("Completa todos los campos", C.amber); return; }
    const updated = { ...projectsData, [pid]: [...(projectsData[pid] || []), entry] };
    saveProjData(updated);
  }

  function removeEntry(pid, id) {
    const updated = { ...projectsData, [pid]: (projectsData[pid] || []).filter(e => e.id !== id) };
    saveProjData(updated);
    setConfirmDel(null);
  }

  function addInfoToProject(pid, text) {
    if (!pid || !text.trim()) return;
    const entry = { id: Date.now(), type: "nota", text: "Corrección del equipo: " + text.trim(), author: userName || "Colaborador", ts: Date.now() };
    const updated = { ...projectsData, [pid]: [...(projectsData[pid] || []), entry] };
    saveProjData(updated);
  }

  async function send() {
    const q = input.trim();
    if (!q && !attached) return;
    if (loading) return;

    setInput("");
    let sid = activeId;
    if (!sid) { const s = newSession(); setSessions(prev => { const next = [s, ...prev]; persist(next); return next; }); sid = s.id; setActiveId(s.id); }

    const userMsg = { role: "user", id: Date.now(), content: q, fileName: attached?.name || null };
    const af = attached; setAttached(null);

    setSessions(prev => {
      const next = prev.map(s => {
        if (s.id !== sid) return s;
        const title = !s.history.length && q ? q.slice(0, 42) + (q.length > 42 ? "..." : "") : s.title;
        return { ...s, history: [...s.history, userMsg], title, project: activeProject, mode };
      });
      persist(next); return next;
    });

    setLoading(true);
    try {
      const msgs = [...history, userMsg].map((m, i, arr) => {
        if (m.role === "user") {
          const isLast = i === arr.length - 1;
          if (isLast && af) {
            return { role: "user", content: [{ type: "text", text: m.content || "Analiza este archivo." }] };
          }
          const raw2 = m.content || "";
          const txt = (typeof raw2 === "string" ? raw2 : Array.isArray(raw2) ? raw2.map(b=>b.text||"").join("") : String(raw2)).trim();
          return txt ? { role: "user", content: txt } : null;
        }
        const raw = m.rawContent || m.respuesta || "";
        const txt = (typeof raw === "string" ? raw : Array.isArray(raw) ? raw.map(b=>b.text||b.content||"").join("") : String(raw)).trim();
        return txt ? { role: "assistant", content: txt } : null;
      }).filter(Boolean);

      const body = {
        model: deep ? "claude-opus-4-20250514" : "claude-sonnet-4-20250514",
        max_tokens: mode === "corta" ? 800 : mode === "normal" ? 2000 : 4000,
        system: buildPrompt(prefs, activeProject, projectsData, mode, deep),
        messages: msgs,
      };
      if (deep) body.tools = [{ type: "web_search_20250305", name: "web_search" }];

      // Ensure messages alternate correctly and last is user
      const validMsgs = [];
      for (const msg of msgs) {
        if (validMsgs.length === 0 && msg.role !== "user") continue;
        if (validMsgs.length > 0 && validMsgs[validMsgs.length-1].role === msg.role) {
          // Merge consecutive same-role messages
          const prev = validMsgs[validMsgs.length-1];
          prev.content = (typeof prev.content === "string" ? prev.content : "") + "\n" + (typeof msg.content === "string" ? msg.content : "");
        } else {
          validMsgs.push({...msg});
        }
      }
      body.messages = validMsgs.length > 0 ? validMsgs : msgs;

      const res = await fetch(PROXY, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error("Error del servidor: " + res.status);
      const rd = await res.json();

      if (rd.error) throw new Error(rd.error.message || "Error de la API");

      const rawText = extractText(rd.content) || "{}";
      let parsed;
      try {
        const clean = rawText.replace(/```json|```/g, "").trim();
        const si = clean.indexOf("{"), ei = clean.lastIndexOf("}");
        parsed = JSON.parse(si !== -1 ? clean.slice(si, ei + 1) : clean);
      } catch (e) {
        parsed = { respuesta: rawText, prompt_mejorado: "", punto_clave: "", datos_numericos: [] };
      }

      // Auto-save correction to project
      if (parsed.nueva_info_proyecto && parsed.proyecto_info) {
        addInfoToProject(parsed.proyecto_info, parsed.nueva_info_proyecto);
      }

      const aiMsg = {
        role: "assistant", id: Date.now() + 1,
        rawContent: parsed.respuesta || rawText,
        respuesta: stripCites(parsed.respuesta || (rawText.startsWith("{") ? "Sin respuesta disponible para esta consulta." : rawText)),
        prompt_mejorado: parsed.prompt_mejorado || "",
        prompt_era_optimo: parsed.prompt_era_optimo || false,
        punto_clave: parsed.punto_clave || "",
        tipo: parsed.tipo || "",
        intencion: parsed.intencion || "",
        necesita_aclaracion: parsed.necesita_aclaracion || null,
        premisa_incorrecta: parsed.premisa_incorrecta || null,
        datos_numericos: (parsed.datos_numericos || []).filter(d => typeof d === "string"),
        project: activeProject, mode, deep,
        modelLabel: deep ? "Opus" : "Sonnet",
      };

      updateSession(sid, s => ({ ...s, history: [...s.history.filter(m => m.id !== userMsg.id), userMsg, aiMsg] }));

      // Log
      try {
        const entry = { ts: Date.now(), user: userName || "Anónimo", tipo: parsed.tipo || "General", intencion: parsed.intencion || "", project: activeProject, mode, deep, q: (q || "").slice(0, 80) };
        const newMyLog = [...myLog, entry].slice(-300);
        setMyLog(newMyLog);
        try { localStorage.setItem(K.log, JSON.stringify(newMyLog)); } catch (e) {}
      } catch (e) {}

    } catch (err) {
      const errMsg = { role: "assistant", id: Date.now() + 1, respuesta: "⚠️ " + err.message, prompt_mejorado: "", punto_clave: "", datos_numericos: [], project: activeProject, mode, deep, modelLabel: "" };
      updateSession(sid, s => ({ ...s, history: [...s.history, errMsg] }));
    }
    setLoading(false);
  }

  function handleFeedbackSubmit() {
    if (!feedbackText.trim() || !feedbackPanel) return;
    const msg = history.find(m => m.id === feedbackPanel);
    const pid = msg?.project;
    if (pid && pid !== "general") {
      addInfoToProject(pid, feedbackText);
      toast("Información agregada al proyecto " + (PROJECTS.find(p => p.id === pid)?.label || ""), C.green);
    } else {
      toast("Información guardada en General", C.orange);
      addInfoToProject("cumbres", feedbackText);
    }
    setFeedback(prev => ({ ...prev, [feedbackPanel]: "down" }));
    setFeedbackPanel(null);
    setFeedbackText("");
  }

  function onDrop(e, targetId) {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) return;
    setSessions(prev => {
      const from = prev.findIndex(s => s.id === draggedId);
      const to = prev.findIndex(s => s.id === targetId);
      if (from === -1 || to === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      persist(next); return next;
    });
    setDraggedId(null);
  }

  // ─── Onboarding ──────────────────────────────────────────────
  const STEPS = [
    { icon: "👋", title: "Bienvenido a Prompt Master v2", body: "El agente interno de Grupo GWEP. Detecto tu intención, mejoro tu pregunta y siempre te doy una respuesta útil." },
    { icon: "⚡", title: "General vs Proyectos", body: "Usa General para consultas amplias que mezclan conocimiento de todos los proyectos. Selecciona un proyecto específico para enfocarte en él." },
    { icon: "⚙️", title: "Preferencias", body: "En la pestaña Preferencias defines cómo quieres que te responda: tono, formato, nivel de detalle. Se aplican en cada consulta." },
    { icon: "📁", title: "Base de conocimiento", body: "En Proyectos agrega notas y links con descripción. El agente usa esa información para respuestas más precisas." },
    { icon: "👎", title: "Feedback", body: "Si una respuesta no es correcta, usa 👎 para agregar la información correcta directamente a la base de conocimiento del proyecto." },
    { icon: "🚀", title: "¡Listo!", body: "Escribe tu primera pregunta. Recuerda: el agente siempre te responde o te pide clarificación — nunca silencio." },
  ];

  // ─── Login screen ────────────────────────────────────────────
  if (!userName) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, fontFamily: "'Inter','Segoe UI',sans-serif" }}>
        <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 16, padding: "40px 36px", maxWidth: 380, width: "90%", textAlign: "center" }}>
          <div style={{ fontSize: 36, fontWeight: 800, letterSpacing: "-1px", background: "linear-gradient(135deg,#7c6cfc,#2dd4a0)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginBottom: 12 }}>PM</div>
          <p style={{ fontWeight: 700, fontSize: 20, color: C.text, margin: "0 0 6px" }}>Prompt Master</p>
          <p style={{ fontSize: 13, color: C.muted, margin: "0 0 28px", lineHeight: 1.6 }}>Agente IA interno de Grupo GWEP. Ingresa tu nombre para comenzar.</p>
          <input value={userInput} onChange={e => setUserInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && userInput.trim()) { setUserName(userInput.trim()); try{localStorage.setItem(K.user, userInput.trim());}catch(e){} setOnboardStep(1); } }} placeholder="Tu nombre..." autoFocus
            style={{ width: "100%", background: C.elevated, border: "1px solid " + C.border, borderRadius: 10, color: C.text, padding: "11px 14px", fontSize: 14, outline: "none", fontFamily: "inherit", textAlign: "center", marginBottom: 12, boxSizing: "border-box" }} />
          <button onClick={() => { if (userInput.trim()) { setUserName(userInput.trim()); try{localStorage.setItem(K.user, userInput.trim());}catch(e){} setOnboardStep(1); } }} disabled={!userInput.trim()}
            style={{ width: "100%", background: userInput.trim() ? "linear-gradient(135deg,#7c6cfc,#5b4fd4)" : "transparent", border: "1px solid " + (userInput.trim() ? C.accent : C.border), color: userInput.trim() ? "#fff" : C.dim, borderRadius: 10, padding: "11px", cursor: userInput.trim() ? "pointer" : "default", fontSize: 14, fontWeight: 600 }}>
            Comenzar
          </button>
        </div>
      </div>
    );
  }

  const loadArr = deep ? LOADING_DEEP : LOADING_NORMAL;

  return (
    <>
      <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: C.bg, color: C.text, fontFamily: "'Inter','Segoe UI',sans-serif" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderBottom: "1px solid " + C.border, flexShrink: 0, background: C.surface }}>
          <button onClick={() => setSidebarOpen(v => !v)} style={{ background: "transparent", border: "1px solid " + C.border, color: C.muted, borderRadius: 7, width: 30, height: 30, cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>☰</button>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Prompt Master</span>
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.accent }} />
          <span style={{ fontSize: 12, color: C.muted }}>Hola, {userName}</span>
          <div style={{ marginLeft: "auto", display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
            {[["chat", "Chat", "Ir al chat principal."],
              ["prefs", "Preferencias", "Configura cómo quieres que te responda el agente."],
              ["proyectos", "Proyectos", "Base de conocimiento de los proyectos."],
              ["stats", "Estadísticas", "Tus estadísticas de uso y panel admin."],
              ["perfil", "Perfil", "Tu perfil."]
            ].map(([t, label, tip]) => (
              <Tip key={t} text={tip} dir="down">
                <button onClick={() => setTab(t)} style={{ background: tab === t ? C.accentBg : "transparent", color: tab === t ? C.accent : C.muted, border: "1px solid " + (tab === t ? C.accentBorder : "transparent"), borderRadius: 7, padding: "4px 11px", cursor: "pointer", fontSize: 12, fontWeight: tab === t ? 500 : 400 }}>{label}</button>
              </Tip>
            ))}
            {history.length > 0 && (
              <Tip text="Copiar toda la conversación." dir="down">
                <button onClick={() => { navigator.clipboard.writeText(history.map(m => m.role === "user" ? "Colaborador:\n" + m.content : "PM:\n" + m.respuesta).join("\n\n---\n\n")); toast("Copiado ✓", C.green); }}
                  style={{ background: "transparent", border: "1px solid " + C.border, color: C.muted, borderRadius: 7, padding: "4px 10px", cursor: "pointer", fontSize: 12 }}>Compartir</button>
              </Tip>
            )}
            <Tip text="Ver guía de uso." dir="down">
              <button onClick={() => setOnboardStep(1)} style={{ background: "transparent", border: "1px solid " + C.border, color: C.dim, borderRadius: 7, width: 28, height: 28, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>❓</button>
            </Tip>
          </div>
        </div>

        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

          {/* Sidebar */}
          {sidebarOpen && (
            <div style={{ width: 230, flexShrink: 0, background: C.surface, borderRight: "1px solid " + C.border, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div style={{ padding: "10px 12px", borderBottom: "1px solid " + C.border }}>
                <Tip text="Iniciar nueva conversación." dir="down">
                  <button onClick={createSession} style={{ width: "100%", background: C.accentBg, border: "1px solid " + C.accentBorder, color: C.accent, borderRadius: 8, padding: "7px 10px", cursor: "pointer", fontSize: 13, fontWeight: 500, textAlign: "left" }}>+ Nueva sesión</button>
                </Tip>
              </div>
              <div style={{ padding: "6px 8px 4px", borderBottom: "1px solid " + C.border }}>
                <input value={sidebarSearch} onChange={e => setSidebarSearch(e.target.value)} placeholder="Buscar sesión..." style={{ width: "100%", background: C.elevated, border: "1px solid " + C.border, borderRadius: 7, color: C.text, padding: "5px 9px", fontSize: 11, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
              </div>
              <div style={{ flex: 1, overflowY: "auto", padding: 8 }}>
                {sessions.length === 0 && <p style={{ fontSize: 12, color: C.dim, padding: "8px 4px" }}>Sin sesiones aún.</p>}
                {sessions.filter(s => !sidebarSearch || s.title.toLowerCase().includes(sidebarSearch.toLowerCase())).map(s => {
                  const active = s.id === activeId;
                  const sp = PROJECTS.find(p => p.id === s.project);
                  return (
                    <div key={s.id} draggable onDragStart={() => setDraggedId(s.id)} onDragOver={e => e.preventDefault()} onDrop={e => onDrop(e, s.id)}
                      style={{ marginBottom: 3, borderRadius: 8, background: active ? C.elevated : draggedId === s.id ? "rgba(124,108,252,0.08)" : "transparent", border: "1px solid " + (active ? C.border : draggedId === s.id ? C.accentBorder : "transparent"), cursor: "grab", opacity: draggedId === s.id ? 0.5 : 1, transition: "opacity 0.15s" }}>
                      <button onClick={() => { setActiveId(s.id); setTab("chat"); }} style={{ width: "100%", background: "transparent", border: "none", color: active ? C.text : C.muted, padding: "7px 9px 3px", cursor: "pointer", fontSize: 12, textAlign: "left", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", display: "block" }}>{s.title}</button>
                      <div style={{ display: "flex", gap: 4, padding: "0 8px 5px", alignItems: "center" }}>
                        {sp && <span style={{ fontSize: 10, color: sp.color }}>{sp.emoji}</span>}
                        <span style={{ fontSize: 10, color: C.dim, flex: 1 }}>{new Date(s.createdAt).toLocaleDateString("es-MX", { day: "numeric", month: "short" })}</span>
                        <button onClick={() => deleteSession(s.id)} style={{ background: "transparent", border: "none", color: C.dim, cursor: "pointer", fontSize: 13, padding: "0 2px" }}>×</button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ padding: "10px 12px", borderTop: "1px solid " + C.border }}>
                <p style={{ fontSize: 11, color: C.dim, margin: "0 0 5px" }}>Cambiar nombre</p>
                <div style={{ display: "flex", gap: 5 }}>
                  <input value={userInput} onChange={e => setUserInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && userInput.trim()) { setUserName(userInput.trim()); try{localStorage.setItem(K.user, userInput.trim());}catch(e){} toast("Nombre actualizado"); } }} placeholder="Nombre..." style={{ flex: 1, background: C.elevated, border: "1px solid " + C.border, borderRadius: 7, color: C.text, padding: "5px 8px", fontSize: 12, outline: "none", fontFamily: "inherit" }} />
                  <button onClick={() => { if (userInput.trim()) { setUserName(userInput.trim()); try{localStorage.setItem(K.user, userInput.trim());}catch(e){} toast("Nombre actualizado"); } }} style={{ background: C.accentBg, border: "1px solid " + C.accentBorder, color: C.accent, borderRadius: 7, padding: "5px 8px", cursor: "pointer", fontSize: 12 }}>OK</button>
                </div>
              </div>
            </div>
          )}

          {/* Main content */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>

            {/* Modals */}
            {confirmDel && (
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
                <div style={{ background: C.surface, border: "1px solid rgba(255,94,94,0.35)", borderRadius: 12, padding: 24, maxWidth: 340, width: "90%", textAlign: "center" }}>
                  <p style={{ fontWeight: 600, fontSize: 15, margin: "0 0 8px" }}>¿Eliminar entrada?</p>
                  <p style={{ fontSize: 13, color: C.muted, margin: "0 0 20px" }}>Esta acción no se puede deshacer.</p>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => removeEntry(confirmDel.pid, confirmDel.id)} style={{ flex: 1, background: C.dangerBg, border: "1px solid rgba(255,94,94,0.35)", color: C.danger, borderRadius: 8, padding: "9px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Eliminar</button>
                    <button onClick={() => setConfirmDel(null)} style={{ flex: 1, background: "transparent", border: "1px solid " + C.border, color: C.muted, borderRadius: 8, padding: "9px", cursor: "pointer", fontSize: 13 }}>Cancelar</button>
                  </div>
                </div>
              </div>
            )}

            {fullscreen && (
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
                <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 16, padding: "24px 28px", maxWidth: 760, width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                    <span style={{ fontSize: 13, color: C.muted }}>Vista completa</span>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => { navigator.clipboard.writeText(fullscreen.respuesta); toast("Copiado ✓"); }} style={{ background: C.elevated, border: "1px solid " + C.border, color: C.muted, borderRadius: 7, padding: "4px 11px", cursor: "pointer", fontSize: 12 }}>Copiar</button>
                      <button onClick={() => setFullscreen(null)} style={{ background: "transparent", border: "1px solid " + C.border, color: C.muted, borderRadius: 7, padding: "4px 11px", cursor: "pointer", fontSize: 13 }}>× Cerrar</button>
                    </div>
                  </div>
                  <div style={{ overflowY: "auto", flex: 1, fontSize: 14, lineHeight: 1.8, color: C.text }}>{renderMd(fullscreen.respuesta)}</div>
                </div>
              </div>
            )}

            {feedbackPanel && (
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
                <div style={{ background: C.surface, border: "1px solid rgba(255,94,94,0.3)", borderRadius: 14, padding: 24, maxWidth: 480, width: "90%" }}>
                  <p style={{ fontWeight: 600, fontSize: 15, margin: "0 0 6px" }}>Agregar información al proyecto</p>
                  <p style={{ fontSize: 12, color: C.muted, margin: "0 0 14px" }}>La información que agregues se guardará en la base de conocimiento del proyecto activo y el agente la usará en futuras consultas.</p>
                  <textarea value={feedbackText} onChange={e => setFeedbackText(e.target.value)} rows={4} placeholder="Escribe la información correcta o el dato que faltó..." style={{ width: "100%", background: C.elevated, border: "1px solid " + C.border, borderRadius: 8, color: C.text, padding: "10px 12px", fontSize: 13, resize: "vertical", outline: "none", lineHeight: 1.5, fontFamily: "inherit", boxSizing: "border-box", marginBottom: 12 }} />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={handleFeedbackSubmit} disabled={!feedbackText.trim()} style={{ flex: 1, background: feedbackText.trim() ? "linear-gradient(135deg,#7c6cfc,#5b4fd4)" : "transparent", border: "1px solid " + (feedbackText.trim() ? C.accent : C.border), color: feedbackText.trim() ? "#fff" : C.dim, borderRadius: 8, padding: "9px", cursor: feedbackText.trim() ? "pointer" : "default", fontSize: 13, fontWeight: 600 }}>Guardar en proyecto</button>
                    <button onClick={() => { setFeedbackPanel(null); setFeedbackText(""); }} style={{ background: "transparent", border: "1px solid " + C.border, color: C.muted, borderRadius: 8, padding: "9px 14px", cursor: "pointer", fontSize: 13 }}>Cancelar</button>
                  </div>
                </div>
              </div>
            )}

            {/* Status bar */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid " + C.border, padding: "5px 16px", background: C.surface, flexShrink: 0, fontSize: 11, color: C.dim }}>
              <span style={{ color: proj.color, fontWeight: 500 }}>{proj.emoji} {proj.label}</span>
              <span>·</span>
              <span>{MODES.find(m => m.id === mode)?.label}</span>
              {deep && <><span>·</span><span style={{ color: "#e879a0" }}>🔬 Deep</span></>}
            </div>

            {/* Tab: Preferencias */}
            {tab === "prefs" && (
              <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
                <p style={{ fontWeight: 600, fontSize: 15, margin: "0 0 6px" }}>Preferencias de respuesta</p>
                <p style={{ fontSize: 13, color: C.muted, margin: "0 0 20px", lineHeight: 1.6 }}>Define cómo quieres que el agente te responda. Estas preferencias se aplican en cada consulta automáticamente.</p>
                <div style={{ background: C.surface, border: "1px solid " + C.accentBorder, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                  <p style={{ fontSize: 11, color: C.accent, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", margin: "0 0 10px" }}>Ejemplos de preferencias</p>
                  {["Respóndeme siempre en español de México, con tono profesional pero cercano.", "Prefiero respuestas con ejemplos concretos del sector inmobiliario.", "Soy del área de ventas, enfócate en argumentos comerciales.", "Evita tecnicismos, explica todo de forma simple."].map((ex, i) => (
                    <button key={i} onClick={() => setPrefsInput(ex)} style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: "none", color: C.muted, padding: "5px 0", cursor: "pointer", fontSize: 12, lineHeight: 1.5 }}>→ {ex}</button>
                  ))}
                </div>
                <textarea value={prefsInput} onChange={e => setPrefsInput(e.target.value)} rows={6} placeholder="Escribe tus preferencias aquí..." style={{ width: "100%", background: C.elevated, border: "1px solid " + C.border, borderRadius: 10, color: C.text, padding: "12px 14px", fontSize: 13, resize: "vertical", outline: "none", lineHeight: 1.6, fontFamily: "inherit", boxSizing: "border-box", marginBottom: 12 }} />
                <button onClick={savePrefs} style={{ background: "linear-gradient(135deg,#7c6cfc,#5b4fd4)", border: "none", color: "#fff", borderRadius: 8, padding: "10px 20px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Guardar preferencias</button>
                {prefs && <div style={{ background: C.elevated, borderRadius: 8, padding: "10px 14px", marginTop: 12 }}><p style={{ fontSize: 11, color: C.dim, margin: "0 0 4px" }}>Preferencias activas:</p><p style={{ fontSize: 13, color: C.muted, margin: 0, lineHeight: 1.5 }}>{prefs}</p></div>}
              </div>
            )}

            {/* Tab: Proyectos */}
            {tab === "proyectos" && (
              <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
                <div style={{ width: 160, flexShrink: 0, borderRight: "1px solid " + C.border, padding: "12px 8px", display: "flex", flexDirection: "column", gap: 4, background: C.surface }}>
                  {PROJECTS.filter(p => p.id !== "general").map(p => {
                    const active = activeProj === p.id;
                    const cnt = (projectsData[p.id] || []).length;
                    return (
                      <button key={p.id} onClick={() => { setActiveProj(p.id); setProjSearch(""); }} style={{ background: active ? p.color + "1a" : "transparent", color: active ? p.color : C.muted, border: "1px solid " + (active ? p.color + "55" : C.border), borderRadius: 8, padding: "8px 10px", cursor: "pointer", fontSize: 12, fontWeight: active ? 600 : 400, textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span>{p.emoji} {p.label}</span>
                        {cnt > 0 && <span style={{ background: p.color + "22", color: p.color, borderRadius: 10, padding: "1px 6px", fontSize: 10 }}>{cnt}</span>}
                      </button>
                    );
                  })}
                </div>
                <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
                  {(() => {
                    const p = PROJECTS.find(x => x.id === activeProj) || PROJECTS[1];
                    const entries = (projectsData[activeProj] || []).filter(e => !projSearch || (e.text || e.title || e.url || "").toLowerCase().includes(projSearch.toLowerCase()));
                    return (
                      <>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 16 }}>{p.emoji}</span>
                          <span style={{ fontWeight: 600, fontSize: 15, color: p.color }}>{p.label}</span>
                          <span style={{ fontSize: 12, color: C.dim }}>{(projectsData[activeProj] || []).length} entradas</span>
                          <input value={projSearch} onChange={e => setProjSearch(e.target.value)} placeholder="Buscar..." style={{ marginLeft: "auto", background: C.elevated, border: "1px solid " + C.border, borderRadius: 8, color: C.text, padding: "5px 10px", fontSize: 12, outline: "none", fontFamily: "inherit", width: 140 }} />
                        </div>

                        {/* Add entry */}
                        <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: 16, marginBottom: 20 }}>
                          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                            {[["nota", "📝 Descripción"], ["link", "🔗 Link"]].map(([t, label]) => {
                              const active = projType === t;
                              return <button key={t} onClick={() => setProjType(t)} style={{ background: active ? p.color + "1a" : "transparent", color: active ? p.color : C.dim, border: "1px solid " + (active ? p.color + "55" : C.border), borderRadius: 20, padding: "4px 12px", cursor: "pointer", fontSize: 12, fontWeight: active ? 600 : 400 }}>{label}</button>;
                            })}
                          </div>
                          {projType === "nota" && (
                            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                              <textarea value={projInput} onChange={e => setProjInput(e.target.value)} placeholder={"Agrega una descripción sobre " + p.label + "..."} rows={2} style={{ flex: 1, background: C.elevated, border: "1px solid " + C.border, borderRadius: 8, color: C.text, padding: "8px 11px", fontSize: 13, resize: "none", outline: "none", lineHeight: 1.5, fontFamily: "inherit" }} />
                              <button onClick={addEntry} disabled={!projInput.trim()} style={{ background: projInput.trim() ? "linear-gradient(135deg,#7c6cfc,#5b4fd4)" : "transparent", border: "1px solid " + (projInput.trim() ? C.accent : C.border), color: projInput.trim() ? "#fff" : C.dim, borderRadius: 8, padding: "9px 14px", cursor: projInput.trim() ? "pointer" : "default", fontSize: 13, fontWeight: 500, flexShrink: 0 }}>Guardar</button>
                            </div>
                          )}
                          {projType === "link" && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                              <input value={projLinkTitle} onChange={e => setProjLinkTitle(e.target.value)} placeholder="Título del link" style={{ background: C.elevated, border: "1px solid " + C.border, borderRadius: 8, color: C.text, padding: "7px 11px", fontSize: 13, outline: "none", fontFamily: "inherit" }} />
                              <input value={projInput} onChange={e => setProjInput(e.target.value)} placeholder="https://..." style={{ background: C.elevated, border: "1px solid " + C.border, borderRadius: 8, color: C.text, padding: "7px 11px", fontSize: 13, outline: "none", fontFamily: "monospace" }} />
                              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                                <textarea value={projLinkDesc} onChange={e => setProjLinkDesc(e.target.value)} placeholder="Describe qué contiene este link (el agente usará esta descripción para contexto)..." rows={2} style={{ flex: 1, background: C.elevated, border: "1px solid " + C.accentBorder, borderRadius: 8, color: C.text, padding: "8px 11px", fontSize: 13, resize: "none", outline: "none", lineHeight: 1.5, fontFamily: "inherit" }} />
                                <button onClick={addEntry} disabled={!projInput.trim() || !projLinkTitle.trim() || !projLinkDesc.trim()} style={{ background: (projInput.trim() && projLinkTitle.trim() && projLinkDesc.trim()) ? "linear-gradient(135deg,#7c6cfc,#5b4fd4)" : "transparent", border: "1px solid " + ((projInput.trim() && projLinkTitle.trim() && projLinkDesc.trim()) ? C.accent : C.border), color: (projInput.trim() && projLinkTitle.trim() && projLinkDesc.trim()) ? "#fff" : C.dim, borderRadius: 8, padding: "9px 14px", cursor: "pointer", fontSize: 13, fontWeight: 500, flexShrink: 0 }}>Guardar</button>
                              </div>
                              <p style={{ fontSize: 11, color: C.accent, margin: 0 }}>⚠️ La descripción es obligatoria — el agente la usa para entender el contenido del link.</p>
                            </div>
                          )}
                        </div>

                        {/* Entries */}
                        {entries.length === 0
                          ? <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 10, padding: 32, textAlign: "center", color: C.dim, fontSize: 13 }}>{(projectsData[activeProj] || []).length === 0 ? "Sin entradas aún. Agrega notas o links sobre este proyecto." : "No hay entradas que coincidan."}</div>
                          : <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {entries.slice().reverse().map(entry => (
                              <div key={entry.id} style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 10, padding: "12px 14px" }}>
                                <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                                  <span style={{ fontSize: 14, flexShrink: 0 }}>{entry.type === "nota" ? "📝" : "🔗"}</span>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    {entry.type === "nota" && <p style={{ fontSize: 13, color: C.text, margin: 0, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{entry.text}</p>}
                                    {entry.type === "link" && (
                                      <>
                                        <p style={{ fontSize: 13, color: C.text, margin: "0 0 3px", fontWeight: 500 }}>{entry.title}</p>
                                        <a href={entry.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: C.accent, textDecoration: "none", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entry.url}</a>
                                        {entry.desc && <p style={{ fontSize: 12, color: C.muted, margin: "4px 0 0", fontStyle: "italic" }}>{entry.desc}</p>}
                                      </>
                                    )}
                                    <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                                      <span style={{ fontSize: 10, color: C.dim }}>{entry.author}</span>
                                      <span style={{ fontSize: 10, color: C.dim }}>·</span>
                                      <span style={{ fontSize: 10, color: C.dim }}>{new Date(entry.ts).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })}</span>
                                    </div>
                                  </div>
                                  <button onClick={() => setConfirmDel({ pid: activeProj, id: entry.id })} style={{ background: "none", border: "none", color: C.dim, cursor: "pointer", fontSize: 14, padding: "0 2px" }}>×</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        }
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Tab: Perfil */}
            {tab === "stats" && (
              <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
                {/* ── Personal Stats ── */}
                {(() => {
                  const DAYS = ["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"];
                  const now = new Date();
                  const weekly = Array.from({length:7},(_,i)=>{const d=new Date(now);d.setDate(d.getDate()-(6-i));const cnt=myLog.filter(l=>new Date(l.ts).toDateString()===d.toDateString()).length;return{label:DAYS[d.getDay()],cnt,today:i===6};});
                  const weekMax = Math.max(1,...weekly.map(d=>d.cnt));
                  const topTopics = Object.entries(myLog.reduce((acc,l)=>{if(!l.q)return acc;l.q.toLowerCase().replace(/[^a-záéíóúñ\s]/g,"").split(/\s+/).filter(w=>w.length>4).forEach(w=>{acc[w]=(acc[w]||0)+1;});return acc;},{})).sort((a,b)=>b[1]-a[1]).slice(0,10);
                  const byProj = PROJECTS.map(p=>({...p,cnt:myLog.filter(l=>l.project===p.id).length})).filter(p=>p.cnt>0);
                  const projMax = Math.max(1,...byProj.map(p=>p.cnt));
                  const todayCnt = myLog.filter(l=>new Date(l.ts).toDateString()===now.toDateString()).length;
                  return (
                    <>
                      <p style={{fontWeight:600,fontSize:15,margin:"0 0 16px"}}>Mis estadísticas</p>
                      {/* KPIs */}
                      {(() => {
                          const totalCost = myLog.reduce((s,l)=>s+(l.cost||0),0);
                          return (
                          <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10,marginBottom:16}}>
                            {[["Total consultas",myLog.length,C.accent],["Hoy",todayCnt,C.green],["Sesiones",sessions.length,"#60c8f5"],["Mi inversión",formatCost(totalCost),"#f5a623"]].map(([label,val,color])=>(
                              <div key={label} style={{background:C.surface,border:"1px solid "+C.border,borderRadius:10,padding:"12px 14px"}}>
                                <p style={{fontSize:11,color:C.muted,margin:"0 0 4px"}}>{label}</p>
                                <p style={{fontSize:22,fontWeight:700,color,margin:0}}>{val}</p>
                              </div>
                            ))}
                          </div>
                          );
                        })()}

                      {/* Weekly bar chart */}
                      <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:10,padding:16,marginBottom:16}}>
                        <p style={{fontSize:11,color:C.muted,margin:"0 0 12px",textTransform:"uppercase",letterSpacing:"0.5px"}}>Actividad semanal</p>
                        <div style={{display:"flex",alignItems:"flex-end",gap:6,height:80}}>
                          {weekly.map((d,i)=>{
                            const h=weekMax>0?Math.max(4,Math.round((d.cnt/weekMax)*72)):4;
                            return <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                              <span style={{fontSize:10,color:d.cnt>0?C.text:C.dim}}>{d.cnt||""}</span>
                              <div style={{width:"100%",height:h,background:d.today?"linear-gradient(180deg,#7c6cfc,#5b4fd4)":d.cnt>0?C.elevated:C.border,borderRadius:"3px 3px 0 0"}}/>
                              <span style={{fontSize:10,color:d.today?C.accent:C.dim}}>{d.label}</span>
                            </div>;
                          })}
                        </div>
                      </div>

                      {/* By project */}
                      {byProj.length>0&&<div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:10,padding:16,marginBottom:16}}>
                        <p style={{fontSize:11,color:C.muted,margin:"0 0 12px",textTransform:"uppercase",letterSpacing:"0.5px"}}>Por proyecto</p>
                        {byProj.sort((a,b)=>b.cnt-a.cnt).map(p=>(
                          <div key={p.id} style={{marginBottom:8}}>
                            <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                              <span style={{fontSize:12,color:C.text}}>{p.emoji} {p.label}</span>
                              <span style={{fontSize:12,color:p.color,fontWeight:600}}>{p.cnt}</span>
                            </div>
                            <div style={{height:4,background:C.elevated,borderRadius:2}}>
                              <div style={{height:4,width:Math.round((p.cnt/projMax)*100)+"%",background:p.color,borderRadius:2,transition:"width 0.3s"}}/>
                            </div>
                          </div>
                        ))}
                      </div>}

                      {/* Topic cloud */}
                      {topTopics.length>0&&<div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:10,padding:16,marginBottom:20}}>
                        <p style={{fontSize:11,color:C.muted,margin:"0 0 10px",textTransform:"uppercase",letterSpacing:"0.5px"}}>Temas frecuentes</p>
                        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                          {topTopics.map(([w,cnt],i)=>(
                            <span key={i} style={{background:C.elevated,border:"1px solid "+C.border,borderRadius:20,padding:"3px 10px",fontSize:Math.max(11,Math.min(16,10+cnt*1.5)),color:C.accent}}>{w} <span style={{color:C.dim,fontSize:10}}>{cnt}</span></span>
                          ))}
                        </div>
                      </div>}

                      {/* Clear my log */}
                      {myLog.length>0&&<button onClick={()=>{setMyLog([]);try{localStorage.setItem(K.log,JSON.stringify([]));}catch(e){};toast("Log borrado",C.danger);}} style={{background:C.dangerBg,border:"1px solid rgba(255,94,94,0.25)",color:C.danger,borderRadius:8,padding:"6px 14px",cursor:"pointer",fontSize:12,marginBottom:24}}>Borrar mi historial</button>}

                      {/* ── Admin Panel ── */}
                      <div style={{borderTop:"1px solid "+C.border,paddingTop:20}}>
                        <p style={{fontSize:12,color:C.accent,fontWeight:600,margin:"0 0 12px",textTransform:"uppercase",letterSpacing:"0.6px"}}>Panel Admin</p>
                        {!adminUnlocked?(
                          <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:10,padding:16,maxWidth:360}}>
                            <p style={{fontSize:13,color:C.muted,margin:"0 0 10px"}}>Acceso restringido.</p>
                            <div style={{display:"flex",gap:8}}>
                              <input type="password" value={adminPass} onChange={e=>{setAdminPass(e.target.value);setAdminErr(false);}} onKeyDown={e=>{if(e.key==="Enter"){if(adminPass===ADMIN_PASS){setAdminUnlocked(true);setAdminErr(false);fetch(LOG_ENDPOINT).then(r=>r.json()).then(d=>{setSharedLog(Array.isArray(d)?d:[]);}).catch(()=>{});}else{setAdminErr(true);setAdminPass("");}}}
                              } placeholder="Contraseña..." style={{flex:1,background:C.elevated,border:"1px solid "+(adminErr?"rgba(255,94,94,0.5)":C.border),borderRadius:8,color:C.text,padding:"7px 10px",fontSize:13,outline:"none",fontFamily:"inherit"}}/>
                              <button onClick={()=>{if(adminPass===ADMIN_PASS){setAdminUnlocked(true);setAdminErr(false);fetch(LOG_ENDPOINT).then(r=>r.json()).then(d=>{setSharedLog(Array.isArray(d)?d:[]);}).catch(()=>{});}else{setAdminErr(true);setAdminPass("");}}} style={{background:"linear-gradient(135deg,#7c6cfc,#5b4fd4)",border:"none",color:"#fff",borderRadius:8,padding:"7px 14px",cursor:"pointer",fontSize:13,fontWeight:500}}>Entrar</button>
                            </div>
                            {adminErr&&<p style={{fontSize:12,color:C.danger,margin:"6px 0 0"}}>Contraseña incorrecta.</p>}
                          </div>
                        ):(()=>{
                          const filteredLog = (function(){
                              const n = new Date();
                              if(adminDateFilter === "today") return sharedLog.filter(l => new Date(l.ts).toDateString() === n.toDateString());
                              if(adminDateFilter === "week") { const w = new Date(n); w.setDate(n.getDate()-7); return sharedLog.filter(l => new Date(l.ts) >= w); }
                              if(adminDateFilter === "month") { const m = new Date(n); m.setDate(n.getDate()-30); return sharedLog.filter(l => new Date(l.ts) >= m); }
                              return sharedLog;
                            })();
                          const totalTeam = filteredLog.length;
                          const todayTeam = filteredLog.filter(l=>new Date(l.ts).toDateString()===now.toDateString()).length;
                          const byUser = filteredLog.reduce((acc,l)=>{acc[l.user]=(acc[l.user]||0)+1;return acc;},{});
                          const byUserMax = Math.max(1,...Object.values(byUser));
                          const byProjTeam = PROJECTS.map(p=>({...p,cnt:filteredLog.filter(l=>l.project===p.id).length})).filter(p=>p.cnt>0);
                          const byProjMax = Math.max(1,...byProjTeam.map(p=>p.cnt));
                          const deepUsage = filteredLog.filter(l=>l.deep).length;
                          const weeklyTeam = Array.from({length:7},(_,i)=>{const d=new Date(now);d.setDate(d.getDate()-(6-i));const cnt=filteredLog.filter(l=>new Date(l.ts).toDateString()===d.toDateString()).length;return{label:DAYS[d.getDay()],cnt,today:i===6};});
                          const weekTeamMax = Math.max(1,...weeklyTeam.map(d=>d.cnt));

                          // Export CSV
                          function exportCSV(){
                            const rows=[["Fecha","Usuario","Tipo","Intención","Proyecto","Modo","Deep","Consulta"],...sharedLog.map(l=>[new Date(l.ts).toLocaleString("es-MX"),l.user||"",l.tipo||"",l.intencion||"",l.project||"",l.mode||"",l.deep?"Sí":"No",(l.q||"").replace(/,/g," ")])];
                            const csv=rows.map(r=>r.join(",")).join("\n");
                            const a=document.createElement("a");a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"}));a.download="gwep-stats-"+new Date().toISOString().slice(0,10)+".csv";a.click();
                          }

                          return (
                            <div>
                              <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}><span style={{fontSize:11,color:C.muted,fontWeight:500}}>Período:</span>{[["all","Todo"],["today","Hoy"],["week","7 días"],["month","30 días"]].map(([val,label])=>(<button key={val} onClick={()=>setAdminDateFilter(val)} style={{background:adminDateFilter===val?"rgba(124,108,252,0.15)":"transparent",color:adminDateFilter===val?C.accent:C.dim,border:"1px solid "+(adminDateFilter===val?C.accentBorder:C.border),borderRadius:20,padding:"3px 10px",cursor:"pointer",fontSize:11,fontWeight:adminDateFilter===val?600:400}}>{label}</button>))}</div>
                              {/* Team KPIs */}
                              {(() => {
                              const totalCostTeam = filteredLog.reduce((s,l)=>s+(l.cost||0),0);
                              return (
                              <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10,marginBottom:16}}>
                                {[["Total consultas",totalTeam,C.accent],["Hoy",todayTeam,C.green],["Deep Research",deepUsage,"#e879a0"],["Costo total",formatCost(totalCostTeam),"#f5a623"]].map(([label,val,color])=>(
                                  <div key={label} style={{background:C.surface,border:"1px solid "+C.border,borderRadius:10,padding:"12px 14px"}}>
                                    <p style={{fontSize:11,color:C.muted,margin:"0 0 4px"}}>{label}</p>
                                    <p style={{fontSize:22,fontWeight:700,color,margin:0}}>{val}</p>
                                  </div>
                                ))}
                              </div>
                              );
                            })()}

                              {/* Team weekly */}
                              <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:10,padding:16,marginBottom:16}}>
                                <p style={{fontSize:11,color:C.muted,margin:"0 0 12px",textTransform:"uppercase",letterSpacing:"0.5px"}}>Actividad del equipo — semana</p>
                                <div style={{display:"flex",alignItems:"flex-end",gap:6,height:80}}>
                                  {weeklyTeam.map((d,i)=>{
                                    const h=weekTeamMax>0?Math.max(4,Math.round((d.cnt/weekTeamMax)*72)):4;
                                    return <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                                      <span style={{fontSize:10,color:d.cnt>0?C.text:C.dim}}>{d.cnt||""}</span>
                                      <div style={{width:"100%",height:h,background:d.today?"linear-gradient(180deg,#2dd4a0,#1aaa7e)":d.cnt>0?C.elevated:C.border,borderRadius:"3px 3px 0 0"}}/>
                                      <span style={{fontSize:10,color:d.today?C.green:C.dim}}>{d.label}</span>
                                    </div>;
                                  })}
                                </div>
                              </div>

                              {/* By user */}
                              {Object.keys(byUser).length>0&&<div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:10,padding:16,marginBottom:16}}>
                                <p style={{fontSize:11,color:C.muted,margin:"0 0 12px",textTransform:"uppercase",letterSpacing:"0.5px"}}>Por colaborador</p>
                                {Object.entries(byUser).sort((a,b)=>b[1]-a[1]).map(([user,cnt])=>{
                                  const userCost = filteredLog.filter(l=>l.user===user).reduce((s,l)=>s+(l.cost||0),0);
                                  return (
                                  <div key={user} style={{marginBottom:10}}>
                                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                                      <span style={{fontSize:12,color:C.text}}>{user}</span>
                                      <div style={{display:"flex",gap:10}}>
                                        <span style={{fontSize:12,color:"#f5a623",fontWeight:600}}>{formatCost(userCost)}</span>
                                        <span style={{fontSize:12,color:C.accent,fontWeight:600}}>{cnt} consultas</span>
                                      </div>
                                    </div>
                                    <div style={{height:4,background:C.elevated,borderRadius:2}}>
                                      <div style={{height:4,width:Math.round((cnt/byUserMax)*100)+"%",background:"linear-gradient(90deg,#7c6cfc,#2dd4a0)",borderRadius:2}}/>
                                    </div>
                                  </div>
                                );})}
                              </div>}

                              {/* By project team */}
                              {byProjTeam.length>0&&<div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:10,padding:16,marginBottom:16}}>
                                <p style={{fontSize:11,color:C.muted,margin:"0 0 12px",textTransform:"uppercase",letterSpacing:"0.5px"}}>Por proyecto — equipo</p>
                                {byProjTeam.sort((a,b)=>b.cnt-a.cnt).map(p=>(
                                  <div key={p.id} style={{marginBottom:8}}>
                                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                                      <span style={{fontSize:12,color:C.text}}>{p.emoji} {p.label}</span>
                                      <span style={{fontSize:12,color:p.color,fontWeight:600}}>{p.cnt}</span>
                                    </div>
                                    <div style={{height:4,background:C.elevated,borderRadius:2}}>
                                      <div style={{height:4,width:Math.round((p.cnt/byProjMax)*100)+"%",background:p.color,borderRadius:2}}/>
                                    </div>
                                  </div>
                                ))}
                              </div>}

                              {/* Full history table */}
                              {filteredLog.length>0&&<div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:10,padding:16,marginBottom:16}}>
                                <p style={{fontSize:11,color:C.muted,margin:"0 0 12px",textTransform:"uppercase",letterSpacing:"0.5px"}}>Historial completo ({filteredLog.length} consultas)</p>
                                <div style={{overflowX:"auto"}}>
                                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                                    <thead>
                                      <tr style={{background:"rgba(124,108,252,0.1)"}}>
                                        {["Fecha","Hora","Usuario","Proyecto","Modo","Consulta","Costo"].map(h=>(
                                          <th key={h} style={{padding:"6px 8px",textAlign:"left",color:"#a78bfa",fontWeight:600,whiteSpace:"nowrap",borderBottom:"1px solid rgba(124,108,252,0.3)"}}>{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {[...filteredLog].reverse().slice(0,200).map((l,i)=>{
                                        const d=new Date(l.ts);
                                        const proj=PROJECTS.find(p=>p.id===l.project);
                                        return (
                                          <tr key={i} style={{background:i%2===0?"transparent":"rgba(255,255,255,0.02)",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                                            <td style={{padding:"5px 8px",color:C.dim,whiteSpace:"nowrap"}}>{d.toLocaleDateString("es-MX",{day:"numeric",month:"short"})}</td>
                                            <td style={{padding:"5px 8px",color:C.dim,whiteSpace:"nowrap"}}>{d.toLocaleTimeString("es-MX",{hour:"2-digit",minute:"2-digit"})}</td>
                                            <td style={{padding:"5px 8px",color:C.text,fontWeight:500,whiteSpace:"nowrap"}}>{l.user||"—"}</td>
                                            <td style={{padding:"5px 8px",whiteSpace:"nowrap"}}><span style={{color:proj?.color||C.dim}}>{proj?.emoji} {proj?.label||l.project}</span></td>
                                            <td style={{padding:"5px 8px",color:C.muted,whiteSpace:"nowrap"}}>{l.deep?"🔬 Deep":l.mode}</td>
                                            <td style={{padding:"5px 8px",color:C.muted,maxWidth:240,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.q||"—"}</td>
                                            <td style={{padding:"5px 8px",color:"#f5a623",whiteSpace:"nowrap"}}>{l.cost?formatCost(l.cost):"—"}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                  {filteredLog.length>200&&<p style={{fontSize:11,color:C.dim,margin:"8px 0 0",textAlign:"center"}}>Mostrando últimas 200 de {filteredLog.length} consultas. Exporta CSV para el historial completo.</p>}
                                </div>
                              </div>}

                              {/* Actions */}
                              <div style={{display:"flex",gap:8}}>
                                <button onClick={exportCSV} style={{background:C.accentBg,border:"1px solid "+C.accentBorder,color:C.accent,borderRadius:8,padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:500}}>↓ Exportar CSV</button>
                                <button onClick={()=>{fetch(LOG_ENDPOINT).then(r=>r.json()).then(d=>{setSharedLog(Array.isArray(d)?d:[]);}).catch(()=>{});toast("Actualizado",C.green);}} style={{background:"transparent",border:"1px solid "+C.border,color:C.muted,borderRadius:8,padding:"7px 12px",cursor:"pointer",fontSize:12}}>↻ Actualizar</button>
                                <button onClick={()=>{setAdminUnlocked(false);setAdminPass("");}} style={{background:"transparent",border:"1px solid "+C.border,color:C.dim,borderRadius:8,padding:"7px 12px",cursor:"pointer",fontSize:12}}>Cerrar sesión</button>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {tab === "perfil" && (
              <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
                <div style={{ background: "linear-gradient(135deg,rgba(124,108,252,0.12),rgba(45,212,160,0.08))", border: "1px solid " + C.accentBorder, borderRadius: 14, padding: "20px 24px", marginBottom: 20, display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ width: 52, height: 52, borderRadius: "50%", background: "linear-gradient(135deg,#7c6cfc,#5b4fd4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700, color: "#fff" }}>{userName.charAt(0).toUpperCase()}</div>
                  <div><p style={{ fontWeight: 700, fontSize: 18, margin: "0 0 2px" }}>{userName}</p><p style={{ fontSize: 12, color: C.muted, margin: 0 }}>{prefs ? "Preferencias configuradas" : "Sin preferencias configuradas"}</p></div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {[["Sesiones", sessions.length, C.accent], ["Entradas de proyectos", Object.values(projectsData).flat().length, C.green]].map(([label, val, color]) => (
                    <div key={label} style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 10, padding: "14px 16px" }}>
                      <p style={{ fontSize: 11, color: C.muted, margin: "0 0 4px" }}>{label}</p>
                      <p style={{ fontSize: 22, fontWeight: 700, color, margin: 0 }}>{val}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tab: Chat */}
            {tab === "chat" && (
              <>
                <div style={{ flex: 1, overflowY: "auto", padding: "24px 24px 0" }}>
                  <div style={{ maxWidth: 720, margin: "0 auto" }}>
                    {history.length === 0 && (
                      <div style={{ paddingTop: 60, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, textAlign: "center" }}>
                        <div style={{ fontSize: 32, fontWeight: 800, background: "linear-gradient(135deg,#7c6cfc,#2dd4a0)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>PM</div>
                        <p style={{ fontWeight: 700, fontSize: 20, margin: 0 }}>Prompt Master</p>
                        <p style={{ fontSize: 14, color: C.muted, margin: 0, maxWidth: 340, lineHeight: 1.7 }}>Detecto tu intención, mejoro tu pregunta y siempre te doy una respuesta útil.</p>
                      </div>
                    )}

                    {history.map(msg => {
                      if (msg.role === "user") return (
                        <div key={msg.id} style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
                          <div style={{ background: C.elevated, border: "1px solid " + C.border, borderRadius: "14px 14px 4px 14px", padding: "10px 14px", maxWidth: "72%", fontSize: 14, lineHeight: 1.6 }}>
                            {msg.fileName && <span style={{ fontSize: 11, color: C.dim, display: "block", marginBottom: 4 }}>📎 {msg.fileName}</span>}
                            {msg.content}
                          </div>
                        </div>
                      );

                      const secs = extractSections(msg.respuesta || "");
                      return (
                        <div key={msg.id} style={{ marginBottom: 24 }}>
                          {/* Meta bar */}
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                            <span style={{ fontFamily: "monospace", fontSize: 10, background: C.elevated, border: "1px solid " + C.border, borderRadius: 4, padding: "1px 5px", color: C.accent }}>PM</span>
                            {msg.tipo && <span style={{ fontSize: 11, background: "rgba(124,108,252,0.1)", color: "#a78bfa", borderRadius: 20, padding: "1px 8px" }}>{msg.tipo}</span>}
                            {msg.deep && <span style={{ fontSize: 11, background: "rgba(232,121,160,0.1)", color: "#e879a0", borderRadius: 20, padding: "1px 8px" }}>Web</span>}
                            {msg.modelLabel && <span style={{ fontSize: 11, color: C.dim }}>{msg.modelLabel}</span>}
                            <span style={{ fontSize: 10, color: C.dim, marginLeft: "auto" }}>{new Date(msg.id).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</span>
                          </div>

                          {/* Prompt mejorado */}
                          {msg.prompt_mejorado && (
                            <div style={{ background: "rgba(124,108,252,0.07)", border: "1px solid rgba(124,108,252,0.2)", borderRadius: 8, padding: "8px 12px", marginBottom: 8, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                              <span style={{ color: C.accent, fontWeight: 600, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.5px" }}>Prompt optimizado: </span>{msg.prompt_mejorado}
                            </div>
                          )}

                          {/* Punto clave */}
                          {msg.punto_clave && (
                            <div style={{ background: "rgba(124,108,252,0.13)", border: "1px solid rgba(124,108,252,0.4)", borderRadius: 10, padding: "10px 14px", marginBottom: 10, display: "flex", alignItems: "flex-start", gap: 10 }}>
                              <span style={{ fontSize: 18, flexShrink: 0, lineHeight: 1 }}>💡</span>
                              <div>
                                <p style={{ fontSize: 10, color: "#7c6cfc", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", margin: "0 0 3px" }}>Punto clave</p>
                                <p style={{ fontSize: 13, color: "#d4caff", margin: 0, lineHeight: 1.6, fontWeight: 500 }}>{msg.punto_clave}</p>
                              </div>
                            </div>
                          )}

                          {/* Alerts */}
                          {msg.premisa_incorrecta && <div style={{ background: "rgba(255,94,94,0.07)", border: "1px solid rgba(255,94,94,0.3)", borderRadius: 8, padding: "8px 12px", marginBottom: 8, fontSize: 12, color: C.danger }}>⚠️ <strong>Premisa incorrecta:</strong> {msg.premisa_incorrecta}</div>}
                          {msg.necesita_aclaracion && <div style={{ background: "rgba(245,166,35,0.07)", border: "1px solid rgba(245,166,35,0.3)", borderRadius: 8, padding: "8px 12px", marginBottom: 8, fontSize: 12, color: C.amber }}>💬 <strong>Necesita aclaración:</strong> {msg.necesita_aclaracion}</div>}

                          {/* Section index */}
                          {secs.length >= 3 && (
                            <div style={{ background: "rgba(124,108,252,0.06)", border: "1px solid rgba(124,108,252,0.2)", borderRadius: 8, padding: "8px 12px", marginBottom: 8 }}>
                              <p style={{ fontSize: 10, color: "#7c6cfc", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", margin: "0 0 6px" }}>Contenido</p>
                              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {secs.map((s, si) => (
                                  <button key={si} onClick={() => document.getElementById("s-" + msg.id + "-" + s.idx)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                                    style={{ background: "transparent", border: "none", color: s.sub ? "#888899" : "#a78bfa", cursor: "pointer", fontSize: 11, textAlign: "left", padding: "1px 0", paddingLeft: s.sub ? 12 : 0, fontWeight: s.sub ? 400 : 500 }}>→ {s.title}</button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Response body */}
                          <div style={{ position: "relative" }}>
                            <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: "4px 14px 14px 14px", padding: "12px 14px", fontSize: 14, lineHeight: 1.7, color: C.text }}>
                              {renderMd(msg.respuesta, msg.id)}
                            </div>
                            <Tip text="Ver en pantalla completa.">
                              <button onClick={() => setFullscreen(msg)} style={{ position: "absolute", top: 8, right: 8, background: C.elevated, border: "1px solid " + C.border, color: C.muted, borderRadius: 6, padding: "3px 8px", cursor: "pointer", fontSize: 11 }}>⛶</button>
                            </Tip>
                          </div>

                          {/* Datos numéricos */}
                          {msg.datos_numericos?.length > 0 && (
                            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6 }}>
                              {msg.datos_numericos.map((d, di) => (
                                <span key={di} style={{ background: C.amberBg, border: "1px solid rgba(245,166,35,0.3)", borderRadius: 6, padding: "2px 8px", fontSize: 10, color: C.amber }}>📊 {d}</span>
                              ))}
                            </div>
                          )}

                          {/* Actions */}
                          <div style={{ display: "flex", gap: 6, marginTop: 8, alignItems: "center" }}>
                            <Tip text="Copiar respuesta.">
                              <button onClick={() => { navigator.clipboard.writeText(msg.respuesta); setCopied(msg.id); setTimeout(() => setCopied(null), 1800); toast("Copiado ✓"); }} style={{ background: "transparent", border: "none", color: copied === msg.id ? C.green : C.dim, cursor: "pointer", fontSize: 12, padding: "2px 4px" }}>{copied === msg.id ? "✓ Copiado" : "Copiar"}</button>
                            </Tip>
                            <Tip text="La respuesta no es correcta — agrega la información correcta al proyecto.">
                              <button onClick={() => { setFeedbackPanel(msg.id); setFeedbackText(""); }} style={{ background: feedback[msg.id] ? "rgba(255,94,94,0.12)" : "transparent", border: feedback[msg.id] ? "1px solid rgba(255,94,94,0.35)" : "1px solid transparent", color: feedback[msg.id] ? C.danger : C.dim, cursor: "pointer", fontSize: 14, padding: "2px 8px", borderRadius: 20 }}>👎</button>
                            </Tip>
                          </div>
                        </div>
                      );
                    })}

                    {loading && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, color: C.dim, fontSize: 13 }}>
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.accent, animation: "pulse 1s infinite" }} />
                        {loadArr[loadingIdx]}
                      </div>
                    )}
                    <div ref={bottomRef} />
                  </div>
                </div>

                {/* Input area */}
                <div style={{ padding: "12px 16px", borderTop: "1px solid " + C.border, background: C.surface, flexShrink: 0 }}>
                  <div style={{ maxWidth: 720, margin: "0 auto" }}>
                    {/* Row 1: Tipo de respuesta */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, color: C.dim, fontWeight: 500, whiteSpace: "nowrap", minWidth: 110 }}>Tipo de respuesta:</span>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {MODES.map(m => {
                          const active = mode === m.id;
                          return (
                            <Tip key={m.id} text={m.tip}>
                              <button onClick={() => setMode(m.id)} style={{ background: active ? C.accentBg : "transparent", color: active ? C.accent : C.dim, border: "1px solid " + (active ? C.accentBorder : C.border), borderRadius: 20, padding: "2px 10px", cursor: "pointer", fontSize: 11, fontWeight: active ? 600 : 400 }}>{m.label}</button>
                            </Tip>
                          );
                        })}
                        <Tip text="Busqueda web en tiempo real. Respuestas mas profundas y completas.">
                          <button onClick={() => setDeep(v => !v)} style={{ background: deep ? "rgba(232,121,160,0.12)" : "transparent", color: deep ? "#e879a0" : C.dim, border: "1px solid " + (deep ? "rgba(232,121,160,0.35)" : C.border), borderRadius: 20, padding: "2px 10px", cursor: "pointer", fontSize: 11, fontWeight: deep ? 600 : 400 }}>Busqueda web (Deep)</button>
                        </Tip>
                      </div>
                    </div>
                    {/* Row 2: Proyecto */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, color: C.dim, fontWeight: 500, whiteSpace: "nowrap", minWidth: 110 }}>Proyecto:</span>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {PROJECTS.map(p => {
                          const active = activeProject === p.id;
                          const isGen = p.id === "general";
                          return (
                            <Tip key={p.id} text={isGen ? "Sin contexto de proyecto especifico. Conoce todos los proyectos en general." : "Enfoca la respuesta en " + p.label + "."}>
                              <button onClick={() => setActiveProject(p.id)} style={{ background: active ? (isGen ? "rgba(249,115,22,0.2)" : p.color + "1a") : (isGen ? "rgba(249,115,22,0.06)" : "transparent"), color: active ? (isGen ? C.orange : p.color) : (isGen ? C.orange : C.dim), border: "1px solid " + (active ? (isGen ? "rgba(249,115,22,0.6)" : p.color + "55") : (isGen ? "rgba(249,115,22,0.3)" : C.border)), borderRadius: 20, padding: "2px 10px", cursor: "pointer", fontSize: 11, fontWeight: (isGen || active) ? 600 : 400 }}>{isGen ? "General" : p.label}</button>
                            </Tip>
                          );
                        })}
                      </div>
                    </div>

                    {attached && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.elevated, borderRadius: 8, padding: "6px 10px", marginBottom: 8 }}>
                        <span style={{ fontSize: 12, color: C.accent }}>📎 {attached.name}</span>
                        <button onClick={() => setAttached(null)} style={{ background: "none", border: "none", color: C.dim, cursor: "pointer", fontSize: 14, marginLeft: "auto" }}>×</button>
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 8, alignItems: "flex-end", background: C.elevated, border: "1px solid " + C.border, borderRadius: 12, padding: "8px 12px" }}>
                      <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,.csv,.txt" onChange={e => setAttached(e.target.files?.[0] || null)} style={{ display: "none" }} />
                      <Tip text="Adjuntar archivo (imagen, PDF, CSV, TXT).">
                        <button onClick={() => fileRef.current?.click()} style={{ background: "transparent", border: "none", color: C.dim, cursor: "pointer", fontSize: 16, padding: "4px", flexShrink: 0 }}>📎</button>
                      </Tip>
                      <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Escribe tu consulta..." rows={1}
                        style={{ flex: 1, background: "transparent", border: "none", color: C.text, fontSize: 14, outline: "none", resize: "none", lineHeight: 1.6, fontFamily: "inherit", maxHeight: 120, overflowY: "auto" }} />
                      <Tip text="Enviar consulta (Enter).">
                        <button onClick={send} disabled={!input.trim() && !attached || loading} style={{ background: (input.trim() || attached) && !loading ? "linear-gradient(135deg,#7c6cfc,#5b4fd4)" : "transparent", border: "1px solid " + ((input.trim() || attached) && !loading ? C.accent : C.border), color: (input.trim() || attached) && !loading ? "#fff" : C.dim, borderRadius: 8, padding: "7px 14px", cursor: (input.trim() || attached) && !loading ? "pointer" : "default", fontSize: 13, fontWeight: 500, flexShrink: 0 }}>Enviar</button>
                      </Tip>
                    </div>
                    <p style={{ fontSize: 10, color: C.dim, margin: "5px 0 0" }}>Enter para enviar · Shift+Enter nueva línea</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
      </div>

      {/* Toasts */}
      <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 999, display: "flex", flexDirection: "column", gap: 8, pointerEvents: "none" }}>
        {toasts.map(t => <div key={t.id} style={{ background: C.surface, border: "1px solid " + (t.color || C.green) + "55", borderRadius: 10, padding: "9px 16px", fontSize: 13, color: t.color || C.green, fontWeight: 500, boxShadow: "0 4px 20px rgba(0,0,0,0.4)", minWidth: 160 }}>{t.msg}</div>)}
      </div>

      {/* Onboarding */}
      {onboardStep > 0 && onboardStep <= STEPS.length && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 400 }}>
          <div style={{ background: "#16161a", border: "1px solid rgba(124,108,252,0.35)", borderRadius: 16, padding: "32px 28px", maxWidth: 400, width: "90%", textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>{STEPS[onboardStep - 1].icon}</div>
            <p style={{ fontWeight: 700, fontSize: 17, margin: "0 0 10px", color: "#f0f0f0" }}>{STEPS[onboardStep - 1].title}</p>
            <p style={{ fontSize: 13, color: "#888899", margin: "0 0 24px", lineHeight: 1.7 }}>{STEPS[onboardStep - 1].body}</p>
            <div style={{ display: "flex", gap: 4, justifyContent: "center", marginBottom: 20 }}>
              {STEPS.map((_, i) => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: i === onboardStep - 1 ? "#7c6cfc" : "#44445a" }} />)}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {onboardStep > 1 && <button onClick={() => setOnboardStep(s => s - 1)} style={{ flex: 1, background: "transparent", border: "1px solid rgba(255,255,255,0.07)", color: "#888899", borderRadius: 10, padding: "10px", cursor: "pointer", fontSize: 13 }}>← Anterior</button>}
              {onboardStep < STEPS.length
                ? <button onClick={() => setOnboardStep(s => s + 1)} style={{ flex: 1, background: "linear-gradient(135deg,#7c6cfc,#5b4fd4)", border: "none", color: "#fff", borderRadius: 10, padding: "10px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Siguiente →</button>
                : <button onClick={() => setOnboardStep(0)} style={{ flex: 1, background: "linear-gradient(135deg,#2dd4a0,#1aaa7e)", border: "none", color: "#fff", borderRadius: 10, padding: "10px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>¡Listo!</button>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
